import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Star, CheckCircle, IndianRupee, ShieldCheck,
  CreditCard, Sparkles, FileText, Globe, Banknote, Clock
} from 'lucide-react';

const CreditCardDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/credit-cards/${id}`);
        setCard(res.data);
      } catch (e) {
        toast.error('Card not found');
        navigate('/credit-cards');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  if (loading) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FF6B35' }}>Loading card details...</div>;
  }
  if (!card) return null;

  const isFree = (card.joining_fee || 0) === 0 && (card.annual_fee || 0) === 0;
  const reviewAvg = (card.reviews || []).reduce((s, r) => s + r.rating, 0) / Math.max(1, (card.reviews || []).length);

  return (
    <div style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
      <Navbar />

      <section style={{ background: 'linear-gradient(135deg,#FFF7ED 0%,#FFFFFF 100%)', padding: '4rem 1.5rem 2rem' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <button onClick={() => navigate('/credit-cards')} style={backBtn} data-testid="ccd-back">
            <ArrowLeft size={16} /> Back to credit cards
          </button>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1, minWidth: 280 }}>
              {card.bank_logo && (
                <img src={card.bank_logo} alt={card.bank_name} style={{ width: 64, height: 64, borderRadius: 14, background: '#fff', padding: 8, border: '1px solid #E2E8F0', objectFit: 'contain' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              )}
              <div>
                <div style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 600 }}>{card.bank_name}</div>
                <h1 style={{ fontSize: 'clamp(1.6rem, 4vw, 2.5rem)', color: '#0F172A', margin: 0, lineHeight: 1.15 }}>{card.name}</h1>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                  {(card.tags || []).map((t) => (
                    <span key={t} style={tag}>{t}</span>
                  ))}
                  {isFree && (
                    <span style={{ ...tag, background: '#ECFDF5', color: '#065F46' }}><Sparkles size={11} /> Lifetime Free</span>
                  )}
                </div>
              </div>
            </div>

            <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 14, padding: '1rem 1.25rem', minWidth: 260 }}>
              {card.rating > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#92400E', fontWeight: 700, fontSize: '1rem', marginBottom: 4 }}>
                  <Star size={16} fill="#F59E0B" stroke="#F59E0B" /> {card.rating} / 5
                  <span style={{ color: '#94A3B8', fontWeight: 500, fontSize: '0.78rem' }}>({(card.reviews || []).length} reviews)</span>
                </div>
              )}
              <button onClick={() => navigate(`/enquiry?service=${encodeURIComponent(card.name)}`)} className="primary-button" style={{ width: '100%', justifyContent: 'center', marginTop: 6 }} data-testid="ccd-apply">
                Apply Now <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section style={{ padding: '2rem 1.5rem 5rem' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.25rem' }}>

          {/* Reward Rate */}
          <Block icon={Sparkles} title="Reward Rate" testId="ccd-reward">
            <div style={{ fontSize: '1rem', color: '#0F172A', fontWeight: 600, lineHeight: 1.5 }}>{card.reward_rate}</div>
            {card.welcome_benefit && (
              <div style={{ marginTop: '0.75rem', background: '#FFF7ED', padding: '0.7rem 0.9rem', borderRadius: 10, color: '#9A3412', fontSize: '0.88rem' }}>
                <strong>Welcome bonus:</strong> {card.welcome_benefit}
              </div>
            )}
          </Block>

          {/* Fees */}
          <Block icon={IndianRupee} title="Fees & Charges" testId="ccd-fees">
            <FeeRow label="Joining Fee" value={card.joining_fee > 0 ? `₹${card.joining_fee.toLocaleString('en-IN')}` : 'NIL'} />
            <FeeRow label="Annual Fee" value={card.annual_fee > 0 ? `₹${card.annual_fee.toLocaleString('en-IN')}` : 'NIL'} sub={card.annual_fee_waiver_at > 0 ? `Waived on ₹${(card.annual_fee_waiver_at/100000).toFixed(1)}L annual spend` : null} />
            <FeeRow label="Finance Charges" value={card.interest_rate || '3.5% per month'} />
            <FeeRow label="ATM Withdrawal" value={card.atm_withdrawal_fee} />
            <FeeRow label="Late Payment Fee" value={card.late_payment_fee} />
            <FeeRow label="Foreign Currency Markup" value={card.forex_markup} />
            <FeeRow label="Overlimit Fee" value={card.overlimit_fee} />
          </Block>

          {/* Key Features */}
          <Block icon={ShieldCheck} title="Key Features" testId="ccd-features">
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(card.key_features || []).map((f, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.9rem', color: '#0F172A' }}>
                  <CheckCircle size={16} style={{ color: '#10B981', flexShrink: 0, marginTop: 2 }} /> <span>{f}</span>
                </li>
              ))}
            </ul>
          </Block>

          {/* Eligibility */}
          <Block icon={CreditCard} title="Eligibility" testId="ccd-eligibility">
            <Field label="Minimum Annual Income" value={card.min_income > 0 ? `₹${card.min_income.toLocaleString('en-IN')}` : 'No minimum'} />
            <Field label="Age" value={card.eligibility_age} />
            <Field label="Credit Limit" value={card.credit_limit_range} />
            <Field label="Cash Advance Limit" value={card.cash_advance_limit} />
            <Field label="EMI Conversion" value={card.emi_conversion} />
          </Block>

          {/* Documents */}
          <Block icon={FileText} title="Documents Required" testId="ccd-documents">
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(card.documents_required || []).map((d, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.88rem', color: '#0F172A' }}>
                  <FileText size={14} style={{ color: '#3B82F6', flexShrink: 0, marginTop: 3 }} /> <span>{d}</span>
                </li>
              ))}
            </ul>
          </Block>

          {/* Reviews */}
          <Block icon={Star} title={`Customer Reviews (${(card.reviews || []).length})`} testId="ccd-reviews" wide>
            {(card.reviews || []).length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1rem', padding: '0.6rem 0.9rem', background: '#FFFBEB', borderRadius: 10 }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#92400E' }}>{reviewAvg.toFixed(1)}</div>
                <div style={{ display: 'flex', gap: 2 }}>
                  {[1,2,3,4,5].map((n) => <Star key={n} size={14} fill={n <= reviewAvg ? '#F59E0B' : '#FED7AA'} stroke={n <= reviewAvg ? '#F59E0B' : '#FED7AA'} />)}
                </div>
                <div style={{ color: '#92400E', fontSize: '0.85rem' }}>Based on {(card.reviews || []).length} verified user reviews</div>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.75rem' }}>
              {(card.reviews || []).map((r, i) => (
                <div key={i} style={{ padding: '0.9rem', background: '#F8FAFC', borderRadius: 10, border: '1px solid #E2E8F0' }} data-testid={`ccd-review-${i}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <div style={{ fontWeight: 700, color: '#0F172A', fontSize: '0.9rem' }}>{r.name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 3, color: '#92400E', fontSize: '0.78rem', fontWeight: 700 }}>
                      <Star size={11} fill="#F59E0B" stroke="#F59E0B" /> {r.rating}
                    </div>
                  </div>
                  <div style={{ fontSize: '0.82rem', color: '#475569', lineHeight: 1.5 }}>{r.comment}</div>
                  <div style={{ fontSize: '0.7rem', color: '#94A3B8', marginTop: 6 }}><Clock size={10} style={{ display: 'inline', marginRight: 3 }} /> {r.date}</div>
                </div>
              ))}
            </div>
          </Block>
        </div>
      </section>
    </div>
  );
};

const Block = ({ icon: Icon, title, children, testId, wide }) => (
  <div className="calculator-section" data-testid={testId} style={{ padding: '1.25rem', gridColumn: wide ? '1 / -1' : 'auto' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.85rem' }}>
      <Icon size={18} style={{ color: '#FF6B35' }} />
      <h3 style={{ fontSize: '1.05rem', color: '#0F172A', margin: 0 }}>{title}</h3>
    </div>
    {children}
  </div>
);

const FeeRow = ({ label, value, sub }) => value ? (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '0.5rem 0', borderBottom: '1px dashed #E2E8F0', gap: 8 }}>
    <div style={{ fontSize: '0.82rem', color: '#475569' }}>{label}</div>
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: '0.85rem', color: '#0F172A', fontWeight: 600 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.7rem', color: '#10B981' }}>{sub}</div>}
    </div>
  </div>
) : null;

const Field = ({ label, value }) => value ? (
  <div style={{ marginBottom: '0.6rem' }}>
    <div style={{ fontSize: '0.72rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    <div style={{ fontSize: '0.92rem', color: '#0F172A', fontWeight: 600 }}>{value}</div>
  </div>
) : null;

const backBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#FF6B35', cursor: 'pointer', marginBottom: '1.25rem', fontWeight: 600, fontSize: '0.9rem' };
const tag = { display: 'inline-flex', alignItems: 'center', gap: 3, background: '#FFF7ED', color: '#9A3412', padding: '0.2rem 0.6rem', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700 };

export default CreditCardDetailPage;
